<template>
  <el-drawer
    class="drawer-inner-scroller"
    :model-value="visible"
    destroy-on-close
    size="480"
    @close="close"
  >
    <template #header>
      <h4>
        税费计算器
      </h4>
    </template>
    <el-form
      ref="formRef"
      v-loading="loading"
      class="add-platform-users-form"
      require-asterisk-position="right"
      label-position="top"
      :model="form"
      :rules="formRules"
    >
      <el-form-item v-if="!isCompany" label="选择企业" prop="companyId">
        <el-select
          v-model="form.companyId"
          filterable
          clearable
          placeholder="请选择企业"
          @change="handleCompanyChange"
        >
          <el-option
            v-for="item in companyList"
            :key="item.id"
            :label="item.companyName"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="选择服务主体" prop="productId">
        <el-select
          v-model="form.productId"
          filterable
          clearable
          :disabled="!isCompany && !form.companyId"
          placeholder="请选择服务主体"
        >
          <el-option
            v-for="item in productList"
            :key="item.productId"
            :label="item.productName"
            :value="item.productId"
          />
        </el-select>
      </el-form-item>
      <el-form-item
        prop="settlementType"
      >
        <template #label>
          <span>
            选择计算方式
            <el-tooltip
              class="auth-tooltip"
              effect="light"
              placement="bottom"
            >
              <template #content>
                <div class="config-tooltip">
                  <strong>结算金额：</strong><br>
                  <span class="text-[#8C8C8C]">结算金额为含税的自由职业者实际到手金额，通过结算金额计算企业应支付总金额</span><br>
                  <strong>到账金额：</strong><br>
                  <span class="text-[#8C8C8C]">到账金额为扣除税金的自由职业者实际到手金额，通过到账金额计算企业应支付总金额</span><br>
                </div>
              </template>
              <svg-icon style="vertical-align: -0.3rem;" class="main-primary-text  !w-[16px]  " icon="mingcute:information-line" />
            </el-tooltip>
          </span>
        </template>
        <el-select
          v-model="form.settlementType"
          filterable
          clearable
          placeholder="请选择计算方式"
        >
          <el-option :label="distributionTypeLabel[EDistributionType.SETTLEMENT]" :value="EDistributionType.SETTLEMENT" />
          <el-option :label="distributionTypeLabel[EDistributionType.RECEIVED]" :value="EDistributionType.RECEIVED" />
        </el-select>
      </el-form-item>

      <el-form-item :label="form.settlementType === EDistributionType.SETTLEMENT ? '结算金额' : '到账金额'" prop="money">
        <el-input v-model="form.money" class="w-[400px]" :placeholder="form.settlementType === EDistributionType.SETTLEMENT ? '请输入结算金额' : '请输入到账金额'" />&nbsp;&nbsp;元
      </el-form-item>
    </el-form>
    <div class="drawer-btns">
      <el-button type="primary" :loading="saveBtnLoading" @click="handleSubmit">
        计算
      </el-button>
    </div>
    <div class="w-[480px] h-[20px] bg-[#E5E5E5] ml-[-23px]" />

    <div v-if="Object.keys(priceInfo).length > 0">
      <img src="https://lg5-prod-1314932667.cos.ap-nanjing.myqcloud.com/web-example/%E8%AE%A1%E7%AE%97%E7%BB%93%E6%9E%9C" alt="">
      <el-descriptions
        class="margin-top mt-4"
        :column="1"
        border
        label-width="200px"
      >
        <el-descriptions-item>
          <template #label>
            <div class="cell-item">
              企业应支付金额
              <el-tooltip
                class="auth-tooltip"
                effect="light"
                :content="form.settlementType === EDistributionType.RECEIVED ? '企业应支付金额=自由职业者到账金额+税金+服务费' : '企业应支付金额=结算金额+服务费'"
                placement="top"
              >
                <svg-icon style="vertical-align: -0.3rem;" class="main-primary-text  !w-[16px]  " icon="mingcute:information-line" />
              </el-tooltip>
            </div>
          </template>
          {{ priceInfo.orderAmount }}元
        </el-descriptions-item>
        <el-descriptions-item>
          <template #label>
            <div class="cell-item">
              自由职业者到手金额
              <el-tooltip
                v-if="form.settlementType === EDistributionType.SETTLEMENT"
                class="auth-tooltip"
                effect="light"
                content="自由职业者到手金额=结算金额-税金"
                placement="top"
              >
                <svg-icon style="vertical-align: -0.3rem;" class="main-primary-text  !w-[16px]  " icon="mingcute:information-line" />
              </el-tooltip>
            </div>
          </template>
          {{ priceInfo.receivedAmount }}元
        </el-descriptions-item>
        <el-descriptions-item>
          <template #label>
            <div class="cell-item">
              税金
            </div>
          </template>
          {{ priceInfo.taxAmount }}元
        </el-descriptions-item>
        <el-descriptions-item>
          <template #label>
            <div class="cell-item">
              服务费
            </div>
          </template>
          {{ priceInfo.serviceAmount }}元
        </el-descriptions-item>
      </el-descriptions>
    </div>
  </el-drawer>
</template>

<script setup lang="ts">
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { checkNumber } from '@core/tools';
import { computed, ref, watch } from 'vue';
import { distributionTypeLabel, EDistributionType, IEnterpriseItem } from '@core/main/types';
import { queryAllProductAuthList, queryEnterpriseList, taxCalculator, useUserStore } from '@core/main';
import { ESystemType } from '@core/api';
import { SvgIcon } from '@vue3/components';

const emit = defineEmits(['close', 'success']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },

});
const { state: userState } = useUserStore();
const isCompany = computed(() => userState?.identity?.systemType === ESystemType.Company);
const productList = ref([]);
const companyList = ref<IEnterpriseItem[]>([]);
const loading = ref(false);
const saveBtnLoading = ref(false);
const formRef = ref<FormInstance>();
const form = ref({} as any);
const priceInfo = ref({} as any);
const formRules = ref<FormRules<any>>({
  companyId: [
    {
      required: true,
      message: '请选择企业',
    },
  ],
  productId: [
    {
      required: true,
      message: '请选择服务主体',
    },
  ],
  settlementType: [
    {
      required: true,
      message: '请选择计算方式',
    },
  ],
  money: [
    {
      required: true,
      message: '请输入到账金额',
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (checkNumber(String(form.value.money), '到账金额', 2)) {
          callback(checkNumber(String(form.value.money), '到账金额', 2));
        }
        callback();
      },
    },
  ],
});

async function doSubmit() {
  saveBtnLoading.value = true;

  try {
    const res = await taxCalculator(form.value);

    priceInfo.value = res;

  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  saveBtnLoading.value = false;
}

function handleSubmit() {
  formRef.value.validate((valid) => {
    if (valid) doSubmit();
  });
}

function close() {
  emit('close');
  formRef.value?.resetFields();
}

async function getEnterpriseList() {
  try {
    const { data } = await queryEnterpriseList({ containDraft: 1 });

    companyList.value = data;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function getProductList() {
  const data = await queryAllProductAuthList({
    dutyType: 1,
    authStatus: 1,
    companyId: form.value.companyId,
  });

  productList.value = data;

}

function handleCompanyChange(val: number) {
  form.value.productId = null;
  getProductList();
}

watch(
  [() => props.visible, isCompany],
  async () => {
    if (props.visible) {
      loading.value = true;
      await getEnterpriseList();

      loading.value = false;
      if (isCompany.value) getProductList();
    } else {
      formRef.value?.resetFields();
      priceInfo.value = {};
    }
  },
);
</script>

<style lang="scss" scoped>
.rate-label{
  @apply flex items-center;

  .rate-tag{
    @apply text-[12px] px-[8px] ml-[7px];

    :deep(.el-tag__content){
      @apply flex items-center;
    }

    .svg-icon{
      @apply w-[12px] h-[12px] mr-[5px];
    }
  }
}
</style>
